/**
 * `useTour()` — orchestration hook for the first-run product tour.
 *
 * Combines:
 *   - Server-side completion state (users.onboarding_completed_at, via
 *     useOnboardingState).
 *   - Per-tab dismissal flag in sessionStorage (so dismissing on a slow
 *     network doesn't replay the tour while the PATCH is in flight, and
 *     refreshing the same tab doesn't replay it either).
 *   - Role → step list mapping.
 *
 * Returns the resolved step list (empty if the tour shouldn't show), an
 * `active` flag, and `dismiss()`. Layout consumes this via `<TourProvider>`
 * and renders `<Tour>` when active.
 */
import { useCallback, useMemo } from 'react'
import { useDismissTour, useOnboardingState } from '../../lib/api/onboarding'
import { type Role, useAuth } from '../../lib/auth'
import type { TourStep } from './TourStep'
import { ACTOR_TOUR } from './tours/actor-tour'
import { PRODUCER_TOUR } from './tours/producer-tour'
import { SUPERVISOR_TOUR } from './tours/supervisor-tour'

const DISMISS_KEY = 'ohear.tour-dismissed'
/** Bump when the tour content materially changes; an old { version } in
 *  localStorage will be ignored and the tour replays. */
const CURRENT_TOUR_VERSION = 1

interface DismissedRecord {
  dismissed: boolean
  version: number
}

export interface UseTourResult {
  /** True when the overlay should render. */
  active: boolean
  /** The role-appropriate step list (empty when no tour applies). */
  steps: readonly TourStep[]
  /** Mark the tour complete on the server + this tab. Safe to call repeatedly. */
  dismiss: () => void
}

function tourForRole(role: Role | null): readonly TourStep[] {
  switch (role) {
    case 'actor':
      return ACTOR_TOUR
    case 'supervisor':
      return SUPERVISOR_TOUR
    case 'owner':
    case 'member':
      return PRODUCER_TOUR
    default:
      return []
  }
}

/** True when the e2e harness has flipped `window.__OHEAR_TEST_MODE__ = true`
 *  before the SPA mounts. We never show the tour in this mode so playwright
 *  selectors like `[data-tour="record-button"]` aren't covered by the overlay. */
function isTestMode(): boolean {
  if (typeof window === 'undefined') return false
  return Boolean((window as unknown as { __OHEAR_TEST_MODE__?: boolean }).__OHEAR_TEST_MODE__)
}

function readDismissedFlag(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    // Back-compat: legacy value '1' (old sessionStorage payload that may have
    // been migrated by hand) — treat as dismissed at version 1.
    if (raw === '1') return true
    const parsed = JSON.parse(raw) as Partial<DismissedRecord>
    if (!parsed?.dismissed) return false
    // Version mismatch → tour content changed; force-show.
    if (parsed.version !== CURRENT_TOUR_VERSION) return false
    return true
  } catch {
    return false
  }
}

function writeDismissedFlag() {
  try {
    const payload: DismissedRecord = { dismissed: true, version: CURRENT_TOUR_VERSION }
    localStorage.setItem(DISMISS_KEY, JSON.stringify(payload))
  } catch {
    // Ignore — localStorage may be blocked in cross-origin iframes / private mode.
  }
}

export function useTour(): UseTourResult {
  const { role, session } = useAuth()
  const onboarding = useOnboardingState()
  const dismissMutation = useDismissTour()

  const steps = useMemo(() => tourForRole(role), [role])

  const active = useMemo(() => {
    // E2E short-circuit: the harness sets __OHEAR_TEST_MODE__ pre-mount so
    // playwright never sees the overlay covering tour-target elements.
    if (isTestMode()) return false
    if (!session) return false
    if (steps.length === 0) return false
    if (!onboarding.data) return false
    if (onboarding.data.onboarding_completed_at) return false
    if (readDismissedFlag()) return false
    return true
  }, [session, steps.length, onboarding.data])

  const dismiss = useCallback(() => {
    writeDismissedFlag()
    // Fire-and-forget; the optimistic cache update in useDismissTour hides the
    // tour immediately. Network failures don't block the user.
    dismissMutation.mutate(undefined, {
      onError: () => {
        // Surfaced via the local sessionStorage flag — the user won't see the
        // tour again this tab even if persistence failed.
      },
    })
  }, [dismissMutation])

  return { active, steps, dismiss }
}
